const mobileCardStateStore = window.__svMobileCardState || (window.__svMobileCardState = {
  cart: null,
  promise: null,
});

const getDesktopCartDrawer = () => document.querySelector('cart-drawer');
const getMobileCartNotification = () => document.querySelector('cart-notification');
const shouldUseMobileCartNotification = () => window.innerWidth < 750;
const getPreferredCartUI = () => {
  if (shouldUseMobileCartNotification()) {
    return getMobileCartNotification() || getDesktopCartDrawer();
  }

  return getDesktopCartDrawer() || getMobileCartNotification();
};

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
		if (this.form) {
			this.form.querySelector('[name=id]').disabled = false;
			this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
		}
        this.cart = getPreferredCartUI();
        // On the cart page, do not use cart drawer/notification – let the page refresh/update instead
        const isCartPage = document.body.classList.contains('template-cart') || document.getElementById('main-cart-items');
        if (isCartPage) {
          this.cart = null;
        }
        this.submitButton = this.querySelector('[type="submit"]') || this.querySelector('.product-form__submit');
        this.mobileQtyRoot = this.closest('[data-card-mobile-qty-root]');
        this.mobileQtyControls = this.mobileQtyRoot?.querySelector('[data-card-mobile-qty-controls]');
        this.mobileQtyValue = this.mobileQtyRoot?.querySelector('[data-card-mobile-qty-value]');
        this.mobileQtyRemoveButton = this.mobileQtyRoot?.querySelector('[data-card-mobile-qty-remove]');
        this.predictiveSearch = this.closest('predictive-search');
        this.mobileQtyCurrent = 0;
        this.mobileQtyLoading = false;

        if (document.querySelector('cart-drawer') && this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // Sync dynamic checkout button state with add to cart button on load
        this.syncDynamicCheckoutButton();
        this.setupMobileCardQty();
      }

      cartUpdateUnsubscriber = undefined;

      connectedCallback() {
        if (!this.mobileQtyRoot) return;

        if (this.predictiveSearch || mobileCardStateStore.cart) {
          this.syncMobileCardQty();
        }

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
          if (event.cartData?.items) {
            this.setSharedMobileCartState(event.cartData);
            this.syncMobileCardQtyFromCart(event.cartData);
            return;
          }

          if (event.productVariantId && event.quantityDelta && event.sourceForm !== this) {
            const variantId = this.getMobileCardVariantId();
            const updatedVariantId = Number.parseInt(event.productVariantId, 10);

            if (variantId === updatedVariantId) {
              this.setMobileCardQtyState(this.mobileQtyCurrent + event.quantityDelta);
            }
          }

          this.syncMobileCardQty(true);
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      syncDynamicCheckoutButton() {
        if (!this.submitButton || !this.form) return;
        
        const dynamicCheckoutButton = this.form.querySelector('.shopify-payment-button__button');
        if (!dynamicCheckoutButton) return;
        
        // If add to cart button is disabled, disable dynamic checkout button too
        if (this.submitButton.hasAttribute('disabled')) {
          dynamicCheckoutButton.setAttribute('disabled', 'disabled');
          dynamicCheckoutButton.setAttribute('aria-disabled', 'true');
        }
      }

      setupMobileCardQty() {
        if (!this.mobileQtyRoot || !this.form || !this.mobileQtyControls) return;

        const initialQty = Number.parseInt(this.mobileQtyRoot.dataset.cardMobileQtyInitial, 10) || 0;
        this.setMobileCardQtyState(initialQty);

        this.mobileQtyRoot.addEventListener('click', (event) => {
          const removeButton = event.target.closest('[data-card-mobile-qty-remove]');
          if (!removeButton) return;

          event.preventDefault();
          event.stopPropagation();
          this.updateMobileCardQuantity(0);
        });
      }

      getMobileCardVariantId() {
        const variantInput = this.form?.querySelector('[name="id"]');
        const variantId = Number.parseInt(variantInput?.value, 10);
        return Number.isNaN(variantId) ? null : variantId;
      }

      getRequestedQuantity(formData) {
        const requestedQuantity = Number.parseInt(formData?.get('quantity'), 10);
        return Number.isNaN(requestedQuantity) || requestedQuantity < 1 ? 1 : requestedQuantity;
      }

      getVariantQtyFromCart(cart, variantId) {
        if (!cart?.items?.length || !variantId) return 0;

        return cart.items.reduce((total, item) => {
          if (item.variant_id === variantId) return total + item.quantity;
          return total;
        }, 0);
      }

      getSharedMobileCartState(forceRefresh = false) {
        if (!forceRefresh && mobileCardStateStore.cart) {
          return Promise.resolve(mobileCardStateStore.cart);
        }

        if (!forceRefresh && mobileCardStateStore.promise) {
          return mobileCardStateStore.promise;
        }

        mobileCardStateStore.promise = fetch(`${routes.cart_url}.js`)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to fetch cart state: ${response.status}`);
            }

            return response.json();
          })
          .then((cart) => {
            mobileCardStateStore.cart = cart;
            return cart;
          })
          .finally(() => {
            mobileCardStateStore.promise = null;
          });

        return mobileCardStateStore.promise;
      }

      setSharedMobileCartState(cart) {
        if (!cart?.items) return;
        mobileCardStateStore.cart = cart;
      }

      setMobileCardQtyState(quantity) {
        if (!this.mobileQtyRoot || !this.mobileQtyControls || !this.mobileQtyValue) return;

        this.mobileQtyCurrent = Math.max(0, Number.parseInt(quantity, 10) || 0);
        this.mobileQtyRoot.dataset.cardMobileQtyInitial = `${this.mobileQtyCurrent}`;
        this.mobileQtyValue.textContent = `${this.mobileQtyCurrent}`;

        if (this.mobileQtyCurrent > 0) {
          this.mobileQtyControls.classList.remove('hidden');
          return;
        }

        this.mobileQtyControls.classList.add('hidden');
        this.resetSubmitButtonState();
      }

      resetSubmitButtonState() {
        if (!this.submitButton) return;

        const defaultContent = this.submitButton.querySelector('span:not(.sold-out-message)');
        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');

        if (defaultContent) {
          defaultContent.classList.remove('hidden');
        }

        if (soldOutMessage) {
          soldOutMessage.classList.add('hidden');
        }

        this.handleErrorMessage();
        this.error = false;

        if (!this.submitButton.hasAttribute('disabled')) {
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      setMobileCardQtyLoading(isLoading) {
        this.mobileQtyLoading = isLoading;
        if (!this.mobileQtyRoot) return;

        this.mobileQtyRoot.classList.toggle('is-loading', isLoading);
        if (this.mobileQtyRemoveButton) this.mobileQtyRemoveButton.disabled = isLoading;
      }

      syncMobileCardQty(forceRefresh = false) {
        if (!this.mobileQtyRoot) return;
        const variantId = this.getMobileCardVariantId();
        if (!variantId) return;

        this.getSharedMobileCartState(forceRefresh)
          .then((cart) => {
            const qty = this.getVariantQtyFromCart(cart, variantId);
            this.setMobileCardQtyState(qty);
          })
          .catch((error) => {
            console.error(error);
          });
      }

      syncMobileCardQtyFromCart(cart) {
        if (!this.mobileQtyRoot) return;

        const variantId = this.getMobileCardVariantId();
        if (!variantId) return;

        const qty = this.getVariantQtyFromCart(cart, variantId);
        this.setMobileCardQtyState(qty);
      }

      updateMobileCardQuantity(nextQuantity) {
        if (!this.mobileQtyRoot || this.mobileQtyLoading) return;

        const variantId = this.getMobileCardVariantId();
        if (!variantId) return;

        const quantity = Math.max(0, Number.parseInt(nextQuantity, 10) || 0);
        const isRemoveAction = quantity === 0;
        const body = isRemoveAction
          ? {
              updates: {
                [variantId]: 0,
              },
            }
          : {
              id: variantId,
              quantity,
            };

        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        this.setMobileCardQtyLoading(true);

        fetch(`${isRemoveAction ? routes.cart_update_url : routes.cart_change_url}`, {
          ...fetchConfig('javascript'),
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status || response.errors) return;

            if (this.cart && this.cart.classList.contains('is-empty') && response.item_count > 0) {
              this.cart.classList.remove('is-empty');
            }

            this.setSharedMobileCartState(response);

            const updatedQty = this.getVariantQtyFromCart(response, variantId);
            this.setMobileCardQtyState(updatedQty);

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            });

            this.updateHeaderCartIcons(response?.sections);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.setMobileCardQtyLoading(false);
          });
      }

      updateHeaderCartIcons(sections) {
        const updateIconInnerHTML = (targetSelector, sourceHTML, sourceSelector) => {
          const targetElement = document.querySelector(targetSelector);
          if (!targetElement || !sourceHTML) return false;

          const sourceDoc = new DOMParser().parseFromString(sourceHTML, 'text/html');
          const sourceElement = sourceDoc.querySelector(sourceSelector);
          if (!sourceElement) return false;

          targetElement.innerHTML = sourceElement.innerHTML;
          return true;
        };

        const updateIconsFromDocument = (doc) => {
          ['#cart-icon-bubble', '#cart-icon-bubble-sticky'].forEach((selector) => {
            const targetElement = document.querySelector(selector);
            const sourceElement = doc.querySelector(selector);

            if (targetElement && sourceElement) {
              targetElement.innerHTML = sourceElement.innerHTML;
            }
          });
        };

        const hasHeaderIcon = Boolean(document.querySelector('#cart-icon-bubble'));
        const hasStickyIcon = Boolean(document.querySelector('#cart-icon-bubble-sticky'));
        const headerUpdated = !hasHeaderIcon || updateIconInnerHTML('#cart-icon-bubble', sections?.['cart-icon-bubble'], '.shopify-section');
        const stickyUpdated =
          !hasStickyIcon ||
          updateIconInnerHTML('#cart-icon-bubble-sticky', sections?.['cart-icon-bubble-sticky'], '#cart-icon-bubble-sticky');

        if (headerUpdated && stickyUpdated) {
          this.triggerCartIconPulse();
          return;
        }

        fetch(window.location.pathname)
          .then((response) => response.text())
          .then((pageHTML) => {
            const pageDoc = new DOMParser().parseFromString(pageHTML, 'text/html');
            updateIconsFromDocument(pageDoc);
            this.triggerCartIconPulse();
          })
          .catch((error) => {
            console.error(error);
          });
      }

      triggerCartIconPulse() {
        const stickyHeader = document.querySelector('sticky-header');
        if (stickyHeader?.reveal) stickyHeader.reveal();

        document.querySelectorAll('.header__icon--cart, #cart-icon-bubble-sticky').forEach((icon) => {
          icon.classList.remove('cart-icon--pulsing');
          void icon.offsetWidth;
          icon.classList.add('cart-icon--pulsing');

          window.clearTimeout(icon.cartPulseTimeout);
          icon.cartPulseTimeout = window.setTimeout(() => {
            icon.classList.remove('cart-icon--pulsing');
          }, 1200);
        });
      }

      syncCartDrawerWithoutOpening(response) {
        const cartDrawer = getDesktopCartDrawer();
        if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;

        cartDrawer.renderContents(response, { open: false });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        this.cart = getPreferredCartUI();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              const defaultContent = this.submitButton.querySelector('span:not(.sold-out-message)');

              // Only open cart drawer on partial add (availability-limited) messages
              const msg = (response?.message || response?.description || '').toString().toLowerCase();
              const looksLikePartialAdd = msg.includes('only') && msg.includes('added') && msg.includes('cart');

              if (looksLikePartialAdd && this.cart) {
                const quickAddModal = this.closest('quick-add-modal');
                
                // Fetch fresh cart HTML and header HTML to update cart drawer and icon bubbles
                Promise.all([
                  fetch(`${routes.cart_url}`).then(r => r.text()),
                  fetch(window.location.pathname).then(r => r.text())
                ]).then(([cartHTML, pageHTML]) => {
                  const cartDoc = new DOMParser().parseFromString(cartHTML, 'text/html');
                  const pageDoc = new DOMParser().parseFromString(pageHTML, 'text/html');
                  
                  // Update cart drawer elements from cart page
                  const cartSelectors = ['cart-drawer-items', '.drawer__footer'];
                  for (const selector of cartSelectors) {
                    const targetElement = document.querySelector(selector);
                    const sourceElement = cartDoc.querySelector(selector);
                    if (targetElement && sourceElement) {
                      targetElement.replaceWith(sourceElement);
                    }
                  }
                  
                  // Update cart icon bubbles from current page
                  const iconSelectors = ['#cart-icon-bubble', '#cart-icon-bubble-sticky'];
                  for (const selector of iconSelectors) {
                    const targetElement = document.querySelector(selector);
                    const sourceElement = pageDoc.querySelector(selector);
                    if (targetElement && sourceElement) {
                      targetElement.innerHTML = sourceElement.innerHTML;
                    }
                  }
                  
                  this.cart.classList.remove('is-empty');
                  this.triggerCartIconPulse();
                  
                  // Close quick-add modal if it exists
                  if (quickAddModal) {
                    quickAddModal.hide(true);
                  }
                })
                .catch((e) => console.error(e));
              } else {
                // True error (e.g., 1000001) – keep drawer closed, just refresh peripheral UI blocks
                this.updateBlocksOnError();
              }
							
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              if (defaultContent) {
                defaultContent.classList.add('hidden');
              }
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const addedQuantity = this.getRequestedQuantity(formData);
            const addedVariantId = formData.get('id');

            if (this.mobileQtyRoot) {
              this.setMobileCardQtyState(this.mobileQtyCurrent + addedQuantity);
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                sourceForm: this,
                productVariantId: addedVariantId,
                quantityDelta: addedQuantity,
                cartData: response,
              });

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              quickAddModal.hide(true);
            }

            this.cart.renderContents(response, { open: true });
            if (this.cart?.tagName === 'CART-NOTIFICATION') {
              this.syncCartDrawerWithoutOpening(response);
            }
            this.triggerCartIconPulse();

            if (this.mobileQtyRoot) {
              // cart/add response may not include full cart items.
              // Sync exact quantity from /cart.js to avoid temporary wrong values.
              this.syncMobileCardQty(true);
            }

          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
						
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }
			
			async updateBlocksOnError() {
				const response = await fetch(`${window.shopUrl}`);
				const text = await response.text();

				const parser = new DOMParser();
				const newDocument = parser.parseFromString(text, 'text/html');

				this.getSectionsToRender().forEach((section) => {
					let newContent;

					if (section.id) {
						newContent = newDocument.getElementById(section.id);
						const currentElement = document.getElementById(section.id);
						if (newContent && currentElement) {
							currentElement.innerHTML = newContent.innerHTML;
						}
					}

					if (section.selector) {
						newContent = newDocument.querySelector(section.selector);
						const currentElement = document.querySelector(section.selector);
						if (newContent && currentElement) {
							currentElement.innerHTML = newContent.innerHTML;
						}
						if (section.selector == 'cart-drawer-items') currentElement?.classList.remove('is-empty')
					}
				});

			}

			getSectionsToRender() {
				return [
					{
						selector: "cart-drawer-items",
					},
					{
						selector: ".drawer__footer",
					},
					{
						id: "cart-icon-bubble"
					}
				];
			}

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
